import { ComponentProps, forwardRef, useEffect, useState } from 'react'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { cva, cx, type VariantProps } from 'class-variance-authority'
import Paragraph from './Paragraph'
import Icon from './Icon'
import { Check } from 'lucide-react'

const checkboxStyles = cva(
    [
        'w-4 h-4 min-w-4 min-h-4 border rounded-sm flex items-center justify-center transition-colors duration-150 ease-in-out'
    ],
    {
        variants: {
            state: {
                default: 'border-borderPrimary bg-white text-black',
                complete: 'border-borderPrimary bg-success text-white',
                error: 'border-borderNegativeSubdued bg-error text-white'
            }
        },
        defaultVariants: {
            state: 'default'
        }
    }
)

type Event = { target: { value: boolean; name: string | undefined } }
type OnChange = (event: Event) => void

type CheckboxProps = Omit<ComponentProps<typeof CheckboxPrimitive.Root>, 'onChange'> &
    VariantProps<typeof checkboxStyles> & {
        label: string
        message?: string
        className?: string
        name?: string
        onChange?: OnChange
        onBlur?: React.FocusEventHandler<HTMLButtonElement> | undefined
    }

const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(
    ({ label, state = 'default', message, className, name, onChange, onBlur, checked, required, ...props }, ref) => {
        const [isChecked, setIsChecked] = useState(checked || false)

        useEffect(() => {
            if (checked !== undefined) {
                setIsChecked(checked)
            }
        }, [checked])

        const handleChange = (checked: boolean) => {
            setIsChecked(checked)

            if (onChange) {
                onChange({
                    target: {
                        value: checked,
                        name
                    }
                })
            }
        }

        return (
            <div className={className}>
                <div className="flex gap-3">
                    <CheckboxPrimitive.Root
                        id={name}
                        name={name}
                        className={cx(checkboxStyles({ state }))}
                        ref={ref}
                        checked={isChecked}
                        onCheckedChange={handleChange}
                        onBlur={onBlur}
                        {...props}
                    >
                        <CheckboxPrimitive.Indicator>
                            <Icon icon={Check} size="1" />
                        </CheckboxPrimitive.Indicator>
                    </CheckboxPrimitive.Root>
                    <div>
                        {label && (
                            <label
                                htmlFor={name}
                                className="block text-base text-textPrimary leading-4"
                                data-testid="__checkbox-label__"
                            >
                                {label}
                                {required && (
                                    <span className="text-textNegative ml-1" data-testid="__input-required-indicator__">
                                        *
                                    </span>
                                )}
                            </label>
                        )}
                        {message && (
                            <Paragraph
                                className="pt-2"
                                size={3}
                                color={state === 'error' ? 'negative' : undefined}
                                data-testid="__checkbox-message__"
                            >
                                {message}
                            </Paragraph>
                        )}
                    </div>
                </div>
            </div>
        )
    }
)

export default Checkbox
